import { Component, inject, ViewChild, OnInit, OnDestroy } from '@angular/core';
import { MatListModule } from '@angular/material/list';
import { MatSidenavModule, MatSidenav } from '@angular/material/sidenav';
import { MatToolbarModule } from '@angular/material/toolbar';
import { Router, RouterModule, NavigationEnd, EventType } from '@angular/router';
import { MatIconModule } from '@angular/material/icon';
import { MatCardModule } from '@angular/material/card';
import { CommonModule } from '@angular/common';
import { LoginService, UserDetails } from '@services/login.service';
import { UtilService } from '@services/util.service';
import { NotificationService } from '@services/notification.service';
import { MatButtonModule } from '@angular/material/button';
import { filter, takeUntil } from 'rxjs/operators';
import { Subject } from 'rxjs';
import { LogService } from '@services/log.service';

@Component({
  selector: 'app-layout',
  standalone: true,
  imports: [
    MatSidenavModule,
    MatListModule,
    RouterModule,
    MatToolbarModule,
    MatIconModule,
    MatCardModule,
    MatButtonModule,
    CommonModule
  ],
  templateUrl: './layout.component.html',
  styleUrl: './layout.component.scss'
})
export class LayoutComponent implements OnInit, OnDestroy {
  @ViewChild('sidenav') sidenav!: MatSidenav;

  #loginService = inject(LoginService);
  #utilService = inject(UtilService);
  #logService = inject(LogService);
  #router = inject(Router);
  #notificationService = inject(NotificationService);

  private destroy$ = new Subject<void>();
  isMobile = false;

  ngOnInit() {
    this.#utilService.isMobile$.subscribe(isMobile => {
      this.isMobile = isMobile;
    });

    // Subscribe to router events and refresh notifications on route changes
    this.#router.events
      .pipe(
        filter(event => event.type === EventType.NavigationEnd),
        takeUntil(this.destroy$)
      )
      .subscribe((event: NavigationEnd) => {
        console.log('Route changed to:', event.url);
        this.#notificationService.refreshNotifications();
      });
  }

  ngOnDestroy() {
    this.destroy$.next();
    this.destroy$.complete();
  }

  toggleMobileMenu(): void {
    this.sidenav?.toggle();
  }

  get loginUser(): UserDetails | null {
    return this.#loginService.loginUser;
  }

  get shouldShowVerificationWarning(): boolean {
    return this.loginUser?.verificationStatus === 'unvalidated';
  }

  onLogout(): void {
    console.log('Logout clicked');
    this.#loginService.logout();
    this.#logService.addUserActionLog('LOGOUT');
  }

  onDeleteAccount(): void {
    console.log('Delete Account clicked');
    
    // Show confirmation dialog using UtilService
    this.#utilService.openPopup(
      'Delete Account',
      'Are you sure you want to delete your account? This action cannot be undone and will permanently remove all your data.',
      'YesOrNo'
    ).subscribe(result => {
      if (result === true) {
        // User confirmed deletion
        console.log('Delete account confirmed');
        this.#loginService.deleteAccount().subscribe({
          next: (success) => {
            if (success) {
              console.log('Account deleted successfully');
              this.#utilService.openSnackBar('Account deleted successfully', 'Close', 'success');
              // Navigation is handled in the service
            } else {
              console.error('Failed to delete account');
              this.#utilService.openSnackBar('Failed to delete account. Please try again.', 'Close', 'error');
            }
          },
          error: (error) => {
            console.error('Error deleting account:', error);
            this.#utilService.openSnackBar('An error occurred while deleting account. Please try again.', 'Close', 'error');
          }
        });
      }
    });
  }
}
